export type SeniorityLevel = 'lead' | 'senior' | 'mid' | 'junior' | 'unknown';

export const SENIORITY_LEVELS: SeniorityLevel[] = ['lead', 'senior', 'mid', 'junior', 'unknown'];

const SENIORITY_TOKENS: Record<Exclude<SeniorityLevel, 'unknown'>, string[]> = {
	lead: ['lead', 'principal', 'staff', 'chief', 'head'],
	senior: ['senior', 'sr', 'snr'],
	mid: ['mid', 'medior', 'intermediate'],
	junior: ['junior', 'jr', 'jnr'],
};

const SENIORITY_RANK: Record<SeniorityLevel, number> = {
	lead: 4,
	senior: 3,
	mid: 2,
	junior: 1,
	unknown: 0,
};

const tokenize = (s: string): string[] =>
	s
		.toLowerCase()
		.replace(/[.,/()&]/g, ' ')
		.replace(/[^\w\s]/g, '')
		.split(/\s+/)
		.filter(Boolean);

export const extractSeniority = (rawTitle: string): { level: SeniorityLevel; baseTitle: string } => {
	const tokens = tokenize(rawTitle);
	if (tokens.length === 0)
		return { level: 'unknown', baseTitle: rawTitle.trim() };

	let level: SeniorityLevel = 'unknown';
	const remaining: string[] = [];

	for (const tok of tokens) {
		let matched = false;
		for (const lvl of Object.keys(SENIORITY_TOKENS) as (keyof typeof SENIORITY_TOKENS)[]) {
			if (SENIORITY_TOKENS[lvl].includes(tok)) {
				if (SENIORITY_RANK[lvl] > SENIORITY_RANK[level])
					level = lvl;
				matched = true;
				break;
			}
		}
		if (!matched)
			remaining.push(tok);
	}

	const baseTitle = remaining.join(' ').trim();
	// If the title was nothing but seniority words, keep the original so we don't collapse to empty.
	return { level, baseTitle: baseTitle.length > 0 ? baseTitle : tokens.join(' ') };
};

export interface PositionCandidate {
	id: string;
	firstName: string;
	lastName: string;
	dates: string;
	experienceId: string;
	originalTitle: string;
	seniority: SeniorityLevel;
}

export type SeniorityBreakdown = Record<SeniorityLevel, number>;

const emptyBreakdown = (): SeniorityBreakdown => ({
	lead: 0,
	senior: 0,
	mid: 0,
	junior: 0,
	unknown: 0,
});

export interface CompanyPosition {
	title: string;
	candidateCount: number;
	candidates: PositionCandidate[];
	companies?: string[];
	seniorityBreakdown: SeniorityBreakdown;
}

// Cross-language role mappings (kept for fuzzy matching after seniority is stripped).
const roleMappings: Record<string, string[]> = {
	functional: ['fonctionnel', 'fonctionnelle'],
	analyst: ['analyste'],
	developer: ['developpeur', 'développeur', 'dev'],
	manager: ['gestionnaire', 'responsable'],
	project: ['projet'],
	designer: ['concepteur'],
	engineer: ['ingenieur', 'ingénieur'],
	consultant: ['conseiller'],
};

const wordsMatch = (a: string, b: string): boolean => {
	if (a === b) return true;
	for (const [key, values] of Object.entries(roleMappings)) {
		const aMatchesGroup = a === key || values.includes(a);
		const bMatchesGroup = b === key || values.includes(b);
		if (aMatchesGroup && bMatchesGroup) return true;
	}
	return false;
};

/**
 * Similarity check used for fuzzy-merging titles AFTER seniority has been stripped.
 *
 * Algorithm:
 *  1. Tokenize each base title; drop tokens shorter than 3 chars.
 *  2. Greedy bipartite match: each token from side A pairs with at most one token from side B (so a shared word is counted once, never twice).
 *  3. Tokens may match exactly OR via the cross-language mapping.
 *  4. similarity = matched / max(words1.length, words2.length); require >= 0.6 AND at least one match.
 */
const isSimilarBaseTitle = (titleA: string, titleB: string): boolean => {
	const words1 = titleA.split(' ').filter(w => w.length > 2);
	const words2 = titleB.split(' ').filter(w => w.length > 2);

	if (words1.length === 0 || words2.length === 0) return false;

	const usedB = new Set<number>();
	let matched = 0;
	for (const a of words1) {
		for (let j = 0; j < words2.length; j++) {
			if (usedB.has(j)) continue;
			if (wordsMatch(a, words2[j])) {
				usedB.add(j);
				matched++;
				break;
			}
		}
	}

	if (matched === 0) return false;
	const similarity = matched / Math.max(words1.length, words2.length);
	return similarity >= 0.6;
};

const normalizeForKey = (title: string): string =>
	title
		.toLowerCase()
		.replace(/[^\w\s]/g, '')
		.replace(/\s+/g, ' ')
		.trim();

interface PositionBucket {
	displayCandidates: { title: string; count: number }[]; // tracks original titles seen for picking the cleanest display
	baseTitle: string;
	candidates: PositionCandidate[];
	companies: Set<string>;
	seniorityBreakdown: SeniorityBreakdown;
}

const pickDisplayTitle = (bucket: { baseTitle: string }): string => {
	if (!bucket.baseTitle) return '';
	return bucket.baseTitle
		.split(' ')
		.map(w => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
		.join(' ');
};

/**
 * Per-company position grouping: counts experiences (one entry per candidate stint).
 * `seniorityBreakdown` therefore sums to `candidateCount`.
 */
export const groupSimilarPositions = (positions: CompanyPosition[]): CompanyPosition[] => {
	const buckets = new Map<string, PositionBucket>();

	positions.forEach(position => {
		const { baseTitle } = extractSeniority(position.title);
		const normalizedBase = normalizeForKey(baseTitle);

		let foundKey: string | null = null;
		for (const key of buckets.keys()) {
			if (key === normalizedBase || isSimilarBaseTitle(normalizedBase, key)) {
				foundKey = key;
				break;
			}
		}

		const bucket = foundKey
			? buckets.get(foundKey)!
			: {
				displayCandidates: [],
				baseTitle: normalizedBase,
				candidates: [],
				companies: new Set<string>(),
				seniorityBreakdown: emptyBreakdown(),
			};

		bucket.displayCandidates.push({ title: baseTitle, count: position.candidateCount });
		bucket.candidates.push(...position.candidates);
		position.candidates.forEach(c => {
			bucket.seniorityBreakdown[c.seniority]++;
		});
		(position.companies || []).forEach(c => bucket.companies.add(c));

		if (!foundKey)
			buckets.set(normalizedBase, bucket);
	});

	return Array.from(buckets.values()).map(bucket => {
		// Pick the display title most strongly represented (highest cumulative candidateCount).
		const titleVotes = new Map<string, number>();
		bucket.displayCandidates.forEach(({ title, count }) => {
			titleVotes.set(title, (titleVotes.get(title) || 0) + count);
		});
		const bestTitle = Array.from(titleVotes.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || bucket.baseTitle;

		return {
			title: pickDisplayTitle({ baseTitle: bestTitle }),
			candidateCount: bucket.candidates.length,
			candidates: sortCandidatesByRecency(bucket.candidates),
			companies: Array.from(bucket.companies),
			seniorityBreakdown: bucket.seniorityBreakdown,
		};
	});
};

// ============================================================================
// Role-centric shape (used by the global /roles page)
// ============================================================================

export interface RoleCompanyBreakdown {
	company: string;
	companyId?: string;
	candidateCount: number;
	candidates: PositionCandidate[];
	seniorityBreakdown: SeniorityBreakdown;
}

export interface RoleRow {
	title: string;
	totalCandidateCount: number;
	companyBreakdown: RoleCompanyBreakdown[];
	candidateNames: string[];
	seniorityBreakdown: SeniorityBreakdown;
}

interface RoleBucket {
	baseTitle: string;
	titleVotes: Map<string, number>;
	candidateIds: Set<string>;
	candidateBestSeniority: Map<string, SeniorityLevel>;
	perCompany: Map<string, {
		displayName: string;
		candidates: Map<string, PositionCandidate>;
		candidateBestSeniority: Map<string, SeniorityLevel>;
	}>;
}

const upgradeSeniority = (current: SeniorityLevel | undefined, next: SeniorityLevel): SeniorityLevel => {
	if (!current) return next;
	return SENIORITY_RANK[next] > SENIORITY_RANK[current] ? next : current;
};

/**
 * Global role grouping: dedupes candidates by id and counts each candidate
 * once at their highest observed seniority within the role. `seniorityBreakdown`
 * therefore sums to `totalCandidateCount`.
 */
export const groupSimilarRoleRows = (rows: RoleRow[]): RoleRow[] => {
	const buckets = new Map<string, RoleBucket>();

	rows.forEach(row => {
		const { baseTitle } = extractSeniority(row.title);
		const normalizedBase = normalizeForKey(baseTitle);

		let foundKey: string | null = null;
		for (const key of buckets.keys()) {
			if (key === normalizedBase || isSimilarBaseTitle(normalizedBase, key)) {
				foundKey = key;
				break;
			}
		}

		const bucket: RoleBucket = foundKey
			? buckets.get(foundKey)!
			: {
				baseTitle: normalizedBase,
				titleVotes: new Map(),
				candidateIds: new Set(),
				candidateBestSeniority: new Map(),
				perCompany: new Map(),
			};

		const rowCandidatesAcrossCompanies = row.companyBreakdown.reduce((s, b) => s + b.candidates.length, 0);
		bucket.titleVotes.set(baseTitle, (bucket.titleVotes.get(baseTitle) || 0) + rowCandidatesAcrossCompanies);

		row.companyBreakdown.forEach(breakdown => {
			const companyKey = breakdown.company.toLowerCase();
			if (!bucket.perCompany.has(companyKey)) {
				bucket.perCompany.set(companyKey, {
					displayName: breakdown.company,
					candidates: new Map(),
					candidateBestSeniority: new Map(),
				});
			}
			const entry = bucket.perCompany.get(companyKey)!;

			breakdown.candidates.forEach(c => {
				if (!entry.candidates.has(c.id))
					entry.candidates.set(c.id, c);
				entry.candidateBestSeniority.set(c.id, upgradeSeniority(entry.candidateBestSeniority.get(c.id), c.seniority));
				bucket.candidateIds.add(c.id);
				bucket.candidateBestSeniority.set(c.id, upgradeSeniority(bucket.candidateBestSeniority.get(c.id), c.seniority));
			});
		});

		if (!foundKey)
			buckets.set(normalizedBase, bucket);
	});

	return Array.from(buckets.values()).map(bucket => {
		const bestTitle = Array.from(bucket.titleVotes.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || bucket.baseTitle;

		const companyBreakdown: RoleCompanyBreakdown[] = Array.from(bucket.perCompany.values())
			.map(({ displayName, candidates, candidateBestSeniority }) => {
				const breakdown = emptyBreakdown();
				candidateBestSeniority.forEach(level => {
					breakdown[level]++;
				});
				return {
					company: displayName,
					candidateCount: candidates.size,
					candidates: sortCandidatesByRecency(
						Array.from(candidates.values()).map(c => ({
							...c,
							seniority: candidateBestSeniority.get(c.id) || c.seniority,
						}))
					),
					seniorityBreakdown: breakdown,
				};
			})
			.sort((a, b) => b.candidateCount - a.candidateCount);

		const candidateNames = Array.from(
			new Set(
				companyBreakdown.flatMap(b => b.candidates.map(c => c.firstName)).filter(Boolean)
			)
		);

		const seniorityBreakdown = emptyBreakdown();
		bucket.candidateBestSeniority.forEach(level => {
			seniorityBreakdown[level]++;
		});

		return {
			title: pickDisplayTitle({ baseTitle: bestTitle }),
			totalCandidateCount: bucket.candidateIds.size,
			companyBreakdown,
			candidateNames,
			seniorityBreakdown,
		};
	});
};

/**
 * Sort candidates within a position: most recent year first, then by name.
 */
export const sortCandidatesByRecency = (candidates: PositionCandidate[]): PositionCandidate[] => {
	return [...candidates].sort((a, b) => {
		const getYear = (dateStr: string): number => {
			if (!dateStr) return 0;
			const yearMatch = dateStr.match(/(\d{4})/);
			return yearMatch ? parseInt(yearMatch[1]) : 0;
		};

		const yearA = getYear(a.dates);
		const yearB = getYear(b.dates);

		if (yearA !== yearB)
			return yearB - yearA;

		const nameA = a.firstName.toLowerCase();
		const nameB = b.firstName.toLowerCase();
		return nameA.localeCompare(nameB);
	});
};
